import { HQ_BASE } from '../constants';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hqStoreService, HqStore } from '../services/hqStoreService';

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  green: { label: '안전', color: 'bg-green-100 text-green-700' },
  yellow: { label: '주의', color: 'bg-yellow-100 text-yellow-700' },
  red: { label: '위험', color: 'bg-red-100 text-red-700' },
};

const SUB_BADGE: Record<string, { label: string; color: string }> = {
  active: { label: '구독중', color: 'bg-green-100 text-green-700' },
  trial: { label: '체험', color: 'bg-blue-100 text-blue-700' },
  suspended: { label: '정지(미납)', color: 'bg-red-100 text-red-700' },
  expired: { label: '만료', color: 'bg-slate-200 text-slate-600' },
};

const HqStoresPage: React.FC = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState<HqStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'franchisee' | 'direct'>('all');
  const [contractTab, setContractTab] = useState<'free' | 'contract' | 'all'>('free');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setStores(await hqStoreService.list(filter === 'all' ? undefined : filter));
    } catch (e: any) {
      setError(e?.response?.data?.detail || '가맹점 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // 1) 계약 탭 필터: 프리(계약 전) / 계약(계약 체결됨) / 전체
  const contractFiltered = stores.filter((s) => {
    if (contractTab === 'all') return true;
    const hasContract = !!s.contract_started_at;
    return contractTab === 'contract' ? hasContract : !hasContract;
  });

  // 2) 검색 필터
  const filtered = search
    ? contractFiltered.filter((s) =>
        [s.name, s.region, s.owner_name].some((v) => v?.toLowerCase().includes(search.toLowerCase()))
      )
    : contractFiltered;

  const freeCount = stores.filter((s) => !s.contract_started_at).length;
  const contractCount = stores.filter((s) => !!s.contract_started_at).length;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">🏪 구독 독서실</h1>
        <button
          onClick={() => navigate(`${HQ_BASE}/stores/new`)}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg"
        >
          + 독서실 등록
        </button>
      </div>

      {/* 계약 단계 탭 */}
      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {[
          { key: 'free' as const, label: '🆓 프리', count: freeCount, desc: '계약 전 / 무료 체험' },
          { key: 'contract' as const, label: '📑 계약', count: contractCount, desc: '정식 계약 체결됨' },
          { key: 'all' as const, label: '전체', count: stores.length, desc: '' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setContractTab(tab.key)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              contractTab === tab.key
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
            title={tab.desc}
          >
            {tab.label} <span className="ml-1 text-xs">({tab.count})</span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap gap-3 items-center">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="all">전체 유형</option>
          <option value="franchisee">가맹점</option>
          <option value="direct">직영점</option>
        </select>
        <input
          type="text"
          placeholder="독서실명·지역·점주 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[180px] px-3 py-2 border rounded-lg text-sm"
        />
        <span className="text-sm text-slate-500">표시 중 {filtered.length}개</span>
      </div>

      {error && (
        <div className="px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-3 text-left">상태</th>
              <th className="px-3 py-3 text-left">독서실명</th>
              <th className="px-3 py-3 text-left">유형</th>
              <th className="px-3 py-3 text-left">구독</th>
              <th className="px-3 py-3 text-left">지역</th>
              <th className="px-3 py-3 text-right">좌석</th>
              <th className="px-3 py-3 text-right">학생</th>
              <th className="px-3 py-3 text-right">점유율</th>
              <th className="px-3 py-3 text-left">점주</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-slate-400">
                  불러오는 중...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-12 text-center text-slate-400">
                  {stores.length === 0 ? (
                    <>
                      등록된 독서실이 없습니다.
                      <br />
                      <Link to={`${HQ_BASE}/stores/new`} className="text-blue-600 hover:underline">
                        + 첫 독서실 등록하기
                      </Link>
                    </>
                  ) : (
                    '검색 결과가 없습니다.'
                  )}
                </td>
              </tr>
            ) : (
              filtered.map((s) => {
                const badge = STATUS_BADGE[s.status_color] || STATUS_BADGE.yellow;
                return (
                  <tr
                    key={s.id}
                    className="border-b last:border-b-0 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`${HQ_BASE}/stores/${s.id}`)}
                  >
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${badge.color}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-medium text-slate-800">{s.name}</td>
                    <td className="px-3 py-3">
                      {s.customer_type === 'sale' ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                          판매형
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
                          구독형
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {s.customer_type === 'sale' ? (
                        <span className="text-xs text-slate-400">해당없음</span>
                      ) : (
                        (() => {
                          const sb = SUB_BADGE[s.subscription_status || ''] || {
                            label: s.subscription_status || '미설정',
                            color: 'bg-slate-100 text-slate-500',
                          };
                          return (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${sb.color}`}>
                              {sb.label}
                            </span>
                          );
                        })()
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-600">{s.region || '-'}</td>
                    <td className="px-3 py-3 text-right">{s.seat_count ?? '-'}</td>
                    <td className="px-3 py-3 text-right font-semibold">{s.student_count}명</td>
                    <td className="px-3 py-3 text-right">
                      {s.occupancy_rate != null ? `${s.occupancy_rate}%` : '-'}
                    </td>
                    <td className="px-3 py-3 text-slate-600">{s.owner_name || '-'}</td>
                    <td className="px-3 py-3 text-right">
                      <span className="text-blue-600 text-xs">상세 →</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-slate-400">
        🟢 안전(50명+) · 🟡 주의(30~49) · 🔴 위험(&lt;30)
      </div>
    </div>
  );
};

export default HqStoresPage;
